<script lang="ts" setup>
import type { DataTableRowKey } from 'naive-ui'
import type { TableConfig } from '~/components/TheTable.vue'
import type { Action } from '~/composables/useRenderButtonPresets'
import type { PrismaOrderBy, PrismaWhere, RouterOutput } from '~/types'

type Person = RouterOutput['persons']['findManyPerson']['data'][number]

const props = defineProps<{
  where?: PrismaWhere<'person'>
  actions?: Action[] | 'false'
  isSelectable?: boolean
  checkedRowKeys?: string[]
  hideAddress?: boolean
  showCreationDate?: boolean
  showEmail?: boolean
  size?: 'small' | 'medium' | 'large'
  showInstitutionMemberCreationDate?: boolean
  defaultOrderBy?: PrismaOrderBy<'person'>[]
}>()
defineEmits<{
  (e: 'select', entity: Person): void
  (e: 'showDetails', entity: Person): void
  (e: 'update', entity: Person): void
  (e: 'remove', entity: Person): void
  (e: 'update:checked-row-keys', keys: DataTableRowKey[]): void
}>()

const { fulltextSearchValue, where } = useFilterData({
  filters: ['/firstName', '/lastName'],
})

const defaultOrderBy: ComputedRef<PrismaOrderBy<'person'>[]> = computed(() => props.defaultOrderBy ?? [{ firstName: 'asc' }])
const orderBy: Ref<PrismaOrderBy<'person'>[] | undefined> = ref()
const { serverSidePaginationTableProps, serverSidePagination, watchForPageCount } = useTablePagination()
const queryParams = useQueryParams('person', {
  where: computed(() => ({
    ...where.value,
    ...props.where,
  })),
  pagination: serverSidePagination,
  orderBy: computed(() => orderBy.value ?? defaultOrderBy.value),
})

const { personQuery } = useQuery()
const { data, isLoading: arePersonsLoading } = personQuery.findMany(queryParams)

const persons = computed(() => data.value?.data)
watchForPageCount(computed(() => data.value?.count ?? 0))

const { $i18n } = useNuxtApp()
const tableColumns = computed((): TableConfig<Person>['columns'] => ([
  {
    title: $i18n.t('account.name.label'),
    key: 'firstName',
    width: 250,
    render: ({ firstName, lastName }) => `${firstName} ${lastName}`,
    sorter: 'default',
  },
  {
    title: $i18n.t('person.birthDate.label'),
    key: 'birthDate',
    width: 100,
    render: ({ birthDate }) => birthDate && $i18n.d(birthDate),
    sorter: 'default',
  },
  {
    title: $i18n.t('account.email.label'),
    key: 'account.email',
    width: 200,
    render: ({ account }) => account && account.email,
    isHidden: !props.showEmail,
    sorter: 'default',
  },
  {
    title: $i18n.t('person.createdAt.label'),
    key: 'createdAt',
    width: 200,
    render: ({ createdAt }) => $i18n.d(createdAt, 'dateTime'),
    isHidden: !props.showCreationDate,
    sorter: 'default',
  },
  {
    title: $i18n.t('institution.membership.role.label'),
    key: 'institutions',
    width: 200,
    render: ({ institutions }) => institutions[0]?.role && $i18n.t(`institution.membership.role.${institutions[0].role}`),
    isHidden: !props.showInstitutionMemberCreationDate,
  },
  {
    title: $i18n.t('person.institutionMember.createdAt.label'),
    key: 'institutions',
    width: 200,
    render: ({ institutions }) => institutions[0]?.createdAt && $i18n.d(institutions[0].createdAt, 'dateTime'),
    isHidden: !props.showInstitutionMemberCreationDate,
  },
  {
    title: $i18n.t('person.address.label'),
    key: 'address.street',
    render: ({ address }) => useAddressString(address),
    isHidden: props.hideAddress,
    sorter: 'default',
    width: 200,
  },
]))

const tableConfig = computed<TableConfig<Person>>(() => ({
  columns: tableColumns.value,
  selectionColumn: props.isSelectable && [{ type: 'selection' }],
  rowKey: ({ id }) => id,
  actionButtons: useFallbackActionButtons(props.actions),
  rowClassName(row) {
    if (props.checkedRowKeys && props.checkedRowKeys.includes(row.id)) {
      return 'n-data-table-tr--striped'
    }
    return ''
  },
}))
</script>

<template>
  <TheSearchBar v-model="fulltextSearchValue" />
  <TheTable
    v-bind="serverSidePaginationTableProps"
    :data="persons"
    :table-config="tableConfig"
    :is-loading="arePersonsLoading"
    :checked-row-keys="checkedRowKeys"
    size="small"
    @select="row => $emit('select', row)"
    @show-details="row => $emit('showDetails', row)"
    @update="row => $emit('update', row)"
    @remove="row => $emit('remove', row)"
    @update:checked-row-keys="keys => $emit('update:checked-row-keys', keys)"
    @update:sorter="(sorter) => {
      orderBy = useTableSorter<'person'>(sorter)
      serverSidePaginationTableProps.controlledPaginationSettings.page = 1
    }"
  />
</template>
